/**
 * Text of the pricing UI, Traditional Chinese.
 *
 *   User Mode   userQuoteText(): price + rough character count + pricing note only.
 *               No token, cost, factor, margin, fee, formula or provider price.
 *   Developer   quoteDevLines() / validationLines() / historyLines():
 *               the complete estimate and validation.
 */

import { PRICING_MODEL_LABEL, PRICING_MODEL_NOTE } from './config';
import type { HistorySummary, PricingValidationRecord } from './history';
import type { QuoteSnapshot } from './quote';
import type { PricingValidation } from './validation';

export const MSG_QUOTE_TEST_PHASE = '目前為測試階段，正式付款功能尚未啟用。';

export function formatTwd(n: number): string {
  return `NT$${Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function formatPercent(rate: number, digits = 1): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(digits)}%`;
}

/** Signed, for estimate errors: "+12.5%" / "−3.0%". */
export function formatSignedPercent(rate: number, digits = 1): string {
  if (!Number.isFinite(rate)) return '—';
  const v = rate * 100;
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits)}%`;
}

export const MSG_QUOTE_PRICE_LABEL = '本次翻譯費用';
export const MSG_QUOTE_NOTE = '價格依文件字數、版面與內容複雜度綜合計算，確認後不再另外加價。';
export const MSG_QUOTE_DETAIL_SUMMARY = '計價方式';
export const MSG_QUOTE_DETAIL =
  '翻譯費用會依文件字數、版面複雜度，以及圖表、公式、雙欄排版等內容綜合計算，因此相同字數的文件價格可能略有不同。系統會在開始翻譯前提供最終價格，確認後不再另外加價。';

/**
 * The character count as a rough figure: PDF extraction counts differently
 * from Word / Google Docs, so the user sees "約 18,500", never an exact
 * number to reconcile against. Hundreds from 1,000 up, tens below.
 */
export function approximateChars(n: number): number {
  if (n <= 0) return 0;
  const step = n >= 1_000 ? 100 : n >= 100 ? 10 : 1;
  return Math.max(step, Math.round(n / step) * step);
}

export interface UserQuoteText {
  priceLabel: string;
  price: string;
  chars: string;
  note: string;
  detailSummary: string;
  detail: string;
  /** The test-phase notice while payment is disabled; null once payment is live. */
  testPhase: string | null;
}

/**
 * The whole User Mode quote: the price, a rough character count and one line
 * on how the price is made. Deliberately nothing else: no token, unit, block
 * or cost; the count is a reference, not a per-character rate.
 */
export function userQuoteText(quote: QuoteSnapshot, options: { testPhase?: boolean } = {}): UserQuoteText {
  return {
    priceLabel: MSG_QUOTE_PRICE_LABEL,
    price: formatTwd(quote.quotedAmount),
    chars: `系統估算：約 ${formatInt(approximateChars(quote.billableChars))} 字`,
    note: MSG_QUOTE_NOTE,
    detailSummary: MSG_QUOTE_DETAIL_SUMMARY,
    detail: MSG_QUOTE_DETAIL,
    testPhase: options.testPhase ? MSG_QUOTE_TEST_PHASE : null,
  };
}

function pagesLabel(pages: readonly number[]): string {
  if (pages.length === 0) return '—';
  return pages.length === 1 ? `第 ${pages[0]} 頁` : `第 ${pages[0]}–${pages[pages.length - 1]} 頁（${pages.length} 頁）`;
}

/** Developer Mode: the estimate behind the price, before any run. */
export function quoteDevLines(q: QuoteSnapshot): string[] {
  return [
    PRICING_MODEL_LABEL,
    PRICING_MODEL_NOTE,
    '',
    `定價版本：${q.pricingVersion}`,
    `報價編號：${q.quoteId}`,
    `報價時間：${q.timestamp}`,
    `報價有效期限：${q.expiresAt}`,
    `文件指紋：${q.documentFingerprint}`,
    `範圍指紋：${q.scopeFingerprint}（${q.scopeMode}）`,
    `選取頁面：${pagesLabel(q.selectedPages)}，PDF 共 ${q.pageCount} 頁（頁數僅供統計，不參與定價）`,
    '',
    '可計費內容（= 實際會送給 provider 的單元）',
    `  可翻譯字元：${formatInt(q.billableChars)}`,
    `  可翻譯單元：${formatInt(q.billableUnits)}（排除 ${formatInt(q.excludedUnits)} 個不送出的單元）`,
    `  內文 ${q.bodyUnits} · 標題 ${q.headingUnits} · 圖表標題 ${q.captionUnits} · 註腳 ${q.footnoteUnits} · 表格儲存格 ${q.tableCellUnits} · 圖內文字 ${q.figureUnits} · 結構式標籤 ${q.structuredLabelUnits} · 側欄 ${q.sidebarUnits} · 其他 ${q.otherUnits}`,
    '',
    '預估 Provider 用量（本地估算，未呼叫 API）',
    `  翻譯：input ${formatInt(q.translationInputTokens)} · output ${formatInt(q.translationOutputTokens)}（預估 ${q.estimatedBatches} 個批次）`,
    `  術語：input ${formatInt(q.terminologyInputTokens)} · output ${formatInt(q.terminologyOutputTokens)}（固定保留）`,
    `  QA：input ${formatInt(q.qaInputTokens)} · output ${formatInt(q.qaOutputTokens)}（保留）`,
    `  預估 Input Token：${formatInt(q.estimatedInputTokens)}`,
    `  預估 Output Token：${formatInt(q.estimatedOutputTokens)}`,
    `  預估 Processed Token：${formatInt(q.estimatedProcessedTokens)}`,
    '',
    '成本與安全售價',
    `  預估 API 成本：${formatTwd(q.estimatedApiCostTwd)}（US$${q.estimatedApiCostUsd.toFixed(4)}）`,
    `  安全成本（× 安全係數）：${formatTwd(q.safeEstimatedApiCostTwd)}`,
    `  預估付款手續費：${formatTwd(q.estimatedPaymentFee)}`,
    `  最低變動成本：${formatTwd(q.minimumVariableCost)}`,
    '',
    `  價格級距：${formatTwd(q.tierPrice)}`,
    `  最低安全售價：${formatTwd(q.minimumSustainablePrice)}`,
    `  最終建議售價：${formatTwd(q.suggestedPrice)}`,
  ];
}

/** Developer Mode: 定價驗證 after the run. */
export function validationLines(v: PricingValidation, quote: QuoteSnapshot): string[] {
  const a = v.actual;
  const lines = [
    PRICING_MODEL_LABEL,
    PRICING_MODEL_NOTE,
    '',
    `定價版本：${v.pricingVersion}`,
    `報價編號：${v.quoteId}`,
    `範圍：${v.scopeFingerprint}（${v.scopeMode}）`,
    '',
    '翻譯前估價',
    `  可翻譯字元：${formatInt(v.billableChars)}`,
    `  可翻譯單元：${formatInt(v.billableUnits)}`,
    `  預估 Input Token：${formatInt(v.estimatedInputTokens)}`,
    `  預估 Output Token：${formatInt(v.estimatedOutputTokens)}`,
    `  預估 Processed Token：${formatInt(v.estimatedProcessedTokens)}`,
    `  預估 API 成本：${formatTwd(v.estimatedApiCostTwd)}`,
    `  安全成本：${formatTwd(v.safeEstimatedApiCostTwd)}`,
    '',
    `  價格級距：${formatTwd(v.tierPrice)}`,
    `  最低安全售價：${formatTwd(v.minimumSustainablePrice)}`,
    `  最終建議售價：${formatTwd(v.suggestedPrice)}`,
    '',
    '翻譯後（provider 實際回報用量）',
    `  實際 Input Token：${formatInt(a.actualInputTokens)}（其中快取 ${formatInt(a.actualCachedInputTokens)}，非快取 ${formatInt(a.actualNonCachedInputTokens)}）`,
    `  實際 Output Token：${formatInt(a.actualOutputTokens)}（含 reasoning ${formatInt(a.actualReasoningTokens)}，不重複計價）`,
    `  實際 Processed Token：${formatInt(a.actualProcessedTokens)}`,
    `  實際 API 成本：${formatTwd(a.actualApiCostTwd)}（US$${a.actualApiCostUsd.toFixed(4)}）`,
    '',
    `  預估付款手續費：${formatTwd(v.estimatedPaymentFee)}`,
    `  實際變動成本：${formatTwd(v.actualVariableCost)}（安全係數不計入實際成本）`,
    '',
    `  毛利：${formatTwd(v.grossProfit)}`,
    `  毛利率：${formatPercent(v.grossMarginRate)}`,
    '',
    `  Token 估算誤差：${formatSignedPercent(v.tokenEstimateErrorRate)}`,
    `  成本估算誤差：${formatSignedPercent(v.costEstimateErrorRate)}`,
    '',
    `定價結果：${v.safetyResult}`,
  ];
  if (!a.usageComplete) lines.push('注意：有階段未回報用量，實際用量被低估。');
  if (v.delivery) {
    const d = v.delivery;
    const sent = d.unitsTotal - d.skippedUnits;
    lines.push(
      '',
      '報價單元 vs 實際送出單元',
      `  報價可計費單元：${formatInt(quote.billableUnits)}`,
      `  實際 provider-bound 單元：${formatInt(sent)}（送出 ${formatInt(d.translatedUnits)} · 快取命中 ${formatInt(d.cachedUnits)} · 失敗 ${formatInt(d.failedUnits)} · 不送出 ${formatInt(d.skippedUnits)}）`,
      v.billableUnitsDelta === 0 ? '  一致：報價與翻譯管線使用同一套 translationEligible 判斷。' : `  差異：${v.billableUnitsDelta! > 0 ? '+' : ''}${v.billableUnitsDelta}（請檢查 translationEligible 與報價的過濾是否一致）`,
    );
    if (d.cachedUnits > 0) lines.push(`  提示：${formatInt(d.cachedUnits)} 個單元由快取提供，本次實際成本低於全新翻譯。`);
  }
  return lines;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const SCOPE_LABEL: Record<PricingValidationRecord['scopeMode'], string> = { all: '整份', chapters: '章節', pages: '頁數' };

/** Developer Mode: 定價測試紀錄 — the newest records and a plain summary. */
export function historyLines(records: readonly PricingValidationRecord[], summary: HistorySummary): string[] {
  const lines = [
    `樣本數：${summary.samples}`,
    `平均成本誤差：${formatSignedPercent(summary.meanCostErrorRate)}`,
    `最大低估幅度：${formatSignedPercent(summary.maxUnderestimateRate)}`,
    `平均毛利率：${formatPercent(summary.meanGrossMarginRate)}`,
    `最低毛利率：${formatPercent(summary.minGrossMarginRate)}`,
    `LOSS 次數：${summary.lossCount}（WARNING ${summary.warningCount}，SAFE ${summary.safeCount}）`,
    '（僅顯示統計，不自動調整價格）',
    '',
    '時間               版本  範圍  字元數    單元  售價    預估成本  實際成本  毛利率   估算誤差  結果',
  ];
  for (const r of records) {
    const pages = r.scopeMode === 'all' ? '' : `(${r.selectedPages.length}p)`;
    lines.push(
      [
        shortTime(r.timestamp).padEnd(18),
        r.pricingVersion.padEnd(5),
        `${SCOPE_LABEL[r.scopeMode] ?? r.scopeMode}${pages}`.padEnd(6),
        formatInt(r.billableChars).padStart(8),
        formatInt(r.billableUnits).padStart(6),
        formatTwd(r.suggestedPrice).padStart(7),
        formatTwd(r.estimatedApiCostTwd).padStart(9),
        formatTwd(r.actualApiCostTwd).padStart(9),
        formatPercent(r.grossMarginRate).padStart(8),
        formatSignedPercent(r.costEstimateErrorRate).padStart(9),
        r.safetyResult,
      ].join(' '),
    );
  }
  if (records.length === 0) lines.push('（尚無紀錄）');
  return lines;
}
